import * as stylex from '@stylexjs/stylex';

// Keyframes
const pulse = stylex.keyframes({
    '0%, 100%': { transform: 'scale(1)', opacity: 0.1 },
    '50%': { transform: 'scale(1.1)', opacity: 0.2 },
});

const shimmer = stylex.keyframes({
    '0%': { left: '-100%' },
    '100%': { left: '100%' },
});

const gradientFlow = stylex.keyframes({
    '0%': { backgroundPosition: '0% 50%' },
    '50%': { backgroundPosition: '100% 50%' },
    '100%': { backgroundPosition: '0% 50%' },
});

const rotate = stylex.keyframes({
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' },
});

const fadeInUp = stylex.keyframes({
    from: { opacity: 0, transform: 'translateY(20px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
});

export const styles = stylex.create({
    /* Homepage Layout */
    homepage: {
        maxWidth: 'var(--max-width)',
        margin: '0 auto',
        padding: {
            default: '0 var(--spacing-md)',
            '@media (max-width: 768px)': '0 var(--spacing-sm)',
        },
    },

    /* Hero Section */
    hero: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: {
            default: 'flex-start',
            '@media (max-width: 992px)': 'center',
        },
        minHeight: '500px',
        position: 'relative',
        maxWidth: '1200px',
        width: '100%',
        flexDirection: {
            default: 'row',
            '@media (max-width: 992px)': 'column',
            '@media (max-width: 768px)': 'column',
        },
        textAlign: {
            default: 'left',
            '@media (max-width: 992px)': 'center',
            '@media (max-width: 768px)': 'center',
        },
        paddingTop: {
            default: null,
            '@media (max-width: 768px)': '4rem',
        },
        marginTop: {
            default: null,
            '@media (max-width: 768px)': 0,
        },
        '::before': {
            content: '""',
            position: 'absolute',
            top: '-50px',
            right: '-50px',
            width: '200px',
            height: '200px',
            borderRadius: '50%',
            backgroundImage: 'linear-gradient(45deg, var(--accent-light, #e0e7ff), var(--primary-light, #dbeafe))',
            opacity: 0.1,
            filter: 'blur(40px)',
            animationName: pulse,
            animationDuration: '6s',
            animationIterationCount: 'infinite',
            animationTimingFunction: 'ease-in-out',
        },
        '::after': {
            content: '""',
            position: 'absolute',
            bottom: '-50px',
            left: '-50px',
            width: '150px',
            height: '150px',
            borderRadius: '50%',
            backgroundImage: 'linear-gradient(45deg, var(--secondary-light, #fef3c7), var(--accent-light, #e0e7ff))',
            opacity: 0.1,
            filter: 'blur(30px)',
            animationName: pulse,
            animationDuration: '8s',
            animationIterationCount: 'infinite',
            animationTimingFunction: 'ease-in-out',
        },
    },

    heroContent: {
        flex: 1,
        width: {
            default: 'auto',
            '@media (max-width: 992px)': '100%',
            '@media (max-width: 768px)': '100%', // Added for mobile
        },
        paddingRight: {
            default: '2rem',
            '@media (max-width: 992px)': 0,
            '@media (max-width: 768px)': 0,
        },
        marginBottom: {
            default: null,
            '@media (max-width: 992px)': '2rem',
            '@media (max-width: 768px)': '2rem',
        },
        position: 'relative',
        zIndex: 1,
    },

    heroTitle: {
        fontSize: {
            default: '3.5rem',
            '@media (max-width: 992px)': '2.75rem',
            '@media (max-width: 768px)': '2.25rem',
            '@media (max-width: 480px)': '1.75rem',
        },
        fontWeight: 800,
        lineHeight: 1.2,
        marginBottom: '1.5rem',
        color: 'var(--text-primary)',
    },

    heroTitleSpan: {
        display: 'inline-block',
    },

    highlight: {
        position: 'relative',
        display: 'inline-block',
        '::after': {
            content: '""',
            position: 'absolute',
            bottom: '0.1em',
            left: 0,
            width: '100%',
            height: '0.3em',
            backgroundColor: 'var(--accent-light, #e0e7ff)',
            zIndex: -1,
            opacity: 0.5,
            transformOrigin: 'left',
        },
    },

    heroSubtitle: {
        fontSize: {
            default: '1.25rem',
            '@media (max-width: 768px)': '1.1rem',
            '@media (max-width: 480px)': '1rem',
        },
        lineHeight: 1.6,
        color: 'var(--text-secondary)',
        marginBottom: '2.5rem',
        maxWidth: {
            default: '90%',
            '@media (max-width: 992px)': '100%',
            '@media (max-width: 768px)': '100%',
        },
    },

    heroButtons: {
        display: 'flex',
        gap: '1rem',
        justifyContent: {
            default: 'flex-start',
            '@media (max-width: 992px)': 'center',
            '@media (max-width: 768px)': 'center',
        },
    },

    primaryButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        backgroundColor: 'var(--accent)',
        color: 'white',
        borderWidth: 0,
        padding: '0.875rem 1.75rem',
        borderRadius: '8px',
        fontSize: '1rem',
        fontWeight: 600,
        textDecoration: 'none',
        position: 'relative',
        overflow: 'hidden',
        zIndex: 1,
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        boxShadow: {
            default: 'none',
            ':hover': '0 10px 20px rgba(0, 0, 0, 0.1)',
        },
        transform: {
            default: 'none',
            ':hover': 'translateY(-3px)',
        },
        '::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '100%',
            height: '100%',
            backgroundImage: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
            zIndex: -1,
            animationName: shimmer,
            animationDuration: '3s',
            animationIterationCount: 'infinite',
        },
    },

    heroImageContainer: {
        flex: 1,
        height: '500px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
    },

    heroImage: {
        width: '90%',
        height: '400px',
        backgroundImage: 'linear-gradient(-45deg, var(--accent-light) 0%, var(--accent) 25%, var(--primary) 50%, var(--secondary) 75%, var(--accent-light) 100%)',
        backgroundSize: '400% 400%',
        borderRadius: 'var(--radius-xl)',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        animationName: gradientFlow,
        animationDuration: '15s',
        animationTimingFunction: 'ease',
        animationIterationCount: 'infinite',
    },

    imageOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        '::before': {
            content: '""',
            position: 'absolute',
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            width: '200px',
            height: '200px',
            animationName: pulse,
            animationDuration: '6s',
            animationIterationCount: 'infinite',
            animationDirection: 'alternate',
        },
        '::after': {
            content: '""',
            position: 'absolute',
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            width: '300px',
            height: '300px',
            opacity: 0.5,
            animationName: pulse,
            animationDuration: '8s',
            animationIterationCount: 'infinite',
            animationDirection: 'alternate-reverse',
        },
    },

    sparkleIcon: {
        color: 'rgba(255, 255, 255, 0.9)',
        filter: 'drop-shadow(0 0 15px rgba(255, 255, 255, 0.5))',
        animationName: rotate,
        animationDuration: '20s',
        animationTimingFunction: 'linear',
        animationIterationCount: 'infinite',
        opacity: 0.9,
        zIndex: 10,
    },

    gridPattern: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px)',
        backgroundSize: '20px 20px',
        zIndex: 1,
    },

    /* Section Titles */
    sectionTitle: {
        fontSize: {
            default: 'var(--font-size-2xl)',
            '@media (max-width: 768px)': 'var(--font-size-xl)',
            '@media (max-width: 480px)': 'var(--font-size-lg)',
        },
        fontWeight: 600,
        color: 'var(--text-primary)',
        marginBottom: 'var(--spacing-xl)',
        textAlign: 'center',
        position: 'relative',
        '::after': {
            content: '""',
            position: 'absolute',
            bottom: '-8px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '60px',
            height: '3px',
            backgroundImage: 'linear-gradient(90deg, var(--primary), var(--accent))',
            borderRadius: 'var(--radius-full)',
        },
    },

    /* Featured AI Models */
    featuredModels: {
        marginBottom: 'var(--spacing-3xl)',
    },

    modelsGrid: {
        display: 'grid',
        gridTemplateColumns: {
            default: 'repeat(auto-fit, minmax(280px, 1fr))',
            '@media (max-width: 768px)': '1fr',
        },
        gap: {
            default: 'var(--spacing-lg)',
            '@media (max-width: 768px)': 'var(--spacing-md)',
        },
        marginTop: 'var(--spacing-xl)',
    },

    /* Model Cards */
    modelCard: {
        backgroundColor: 'var(--bg-surface)',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderRadius: 'var(--radius-lg)',
        padding: {
            default: 'var(--spacing-xl)',
            '@media (max-width: 768px)': 'var(--spacing-lg)',
        },
        transition: 'all var(--transition-normal)',
        position: 'relative',
        overflow: 'hidden',
        backdropFilter: 'blur(4px)',
        transform: {
            default: 'none',
            ':hover': 'translateY(-4px)',
        },
        boxShadow: {
            default: 'none',
            ':hover': 'var(--shadow-lg)',
        },
        // FIXED: Merged static and hover border colors
        borderColor: {
            default: 'var(--border)',
            ':hover': 'var(--primary)',
        },
    },

    modelCardTopBorder: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        backgroundImage: 'linear-gradient(90deg, rgb(66, 153, 240), rgb(245, 61, 153))',
        transform: 'scaleX(0)',
        transformOrigin: 'center',
        zIndex: 10,
        transition: 'transform var(--transition-normal)',
    },

    modelCardTopBorderHover: {
        transform: 'scaleX(1)',
    },

    modelCardHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--spacing-md)',
        marginBottom: 'var(--spacing-xl)',
        width: '100%',
    },

    modelCardIcon: {
        fontSize: 'var(--font-size-2xl)',
        width: '48px',
        height: '48px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 'var(--radius-lg)',
        backgroundColor: 'var(--bg-secondary)',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'var(--border)',
        boxShadow: 'var(--shadow-sm)',
        color: 'inherit',
    },

    /* Model-specific icon colors */
    imageModels: { color: '#ffd966' },
    videoModels: { color: '#ff79c6' },
    speechModels: { color: '#50fa7b' },
    musicModels: { color: '#c2a7fc' },
    soundEffectsModels: { color: '#8be9fd' },
    chatModels: { color: '#4299F0' },

    modelCardTitle: {
        fontSize: 'var(--font-size-lg)',
        fontWeight: 600,
        color: 'var(--text-primary)',
        margin: 0,
    },

    modelCardCategory: {
        fontSize: 'var(--font-size-xs)',
        color: 'var(--text-tertiary)',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        backgroundColor: 'var(--bg-secondary)',
        padding: 'var(--spacing-xs) var(--spacing-sm)',
        borderRadius: 'var(--radius-full)',
    },

    modelCardDescription: {
        color: 'var(--text-secondary)',
        marginBottom: 'var(--spacing-lg)',
        lineHeight: 1.6,
    },

    modelCardFeatures: {
        listStyle: 'none',
        padding: 0,
        margin: '0 0 var(--spacing-lg) 0',
    },

    featureItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-sm)',
        color: 'var(--text-secondary)',
        marginBottom: 'var(--spacing-xs)',
        fontSize: 'var(--font-size-sm)',
        '::before': {
            content: '"✓"',
            color: 'var(--success)',
            fontWeight: 'bold',
            fontSize: 'var(--font-size-sm)',
        },
    },

    modelCardLink: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--spacing-sm)',
        color: {
            default: 'var(--primary)',
            ':hover': 'var(--primary-hover)',
        },
        textDecoration: 'none',
        fontWeight: 500,
        fontSize: 'var(--font-size-sm)',
        transition: 'color var(--transition-normal)',
    },

    guidesGrid: {
        display: 'grid',
        gridTemplateColumns: {
            default: 'repeat(auto-fit, minmax(300px, 1fr))',
            '@media (max-width: 768px)': '1fr',
        },
        gap: {
            default: 'var(--spacing-lg)',
            '@media (max-width: 768px)': 'var(--spacing-md)',
        },
        marginTop: 'var(--spacing-xl)',
    },

    guideCard: {
        backgroundColor: 'var(--bg-secondary)',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderRadius: 'var(--radius-lg)',
        padding: {
            default: 'var(--spacing-xl)',
            '@media (max-width: 768px)': 'var(--spacing-lg)',
        },
        transition: 'all var(--transition-normal)',
        position: 'relative',
        backdropFilter: 'blur(4px)',
        transform: {
            default: 'none',
            ':hover': 'translateY(-2px)',
        },
        boxShadow: {
            default: 'none',
            ':hover': 'var(--shadow-md)',
        },
        // FIXED: Merged static and hover border colors
        borderColor: {
            default: 'var(--border)',
            ':hover': 'var(--primary)',
        },
    },

    guideCardTitle: {
        fontSize: 'var(--font-size-lg)',
        fontWeight: 600,
        color: 'var(--text-primary)',
        marginBottom: 'var(--spacing-md)',
    },

    guideCardText: {
        color: 'var(--text-secondary)',
        marginBottom: 'var(--spacing-lg)',
        lineHeight: 1.6,
    },

    guideLink: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: {
            default: 'var(--spacing-sm)',
            ':hover': 'var(--spacing-md)',
        },
        color: {
            default: 'var(--primary)',
            ':hover': 'var(--primary-hover)',
        },
        textDecoration: 'none',
        fontWeight: 500,
        fontSize: 'var(--font-size-sm)',
        transition: 'all var(--transition-normal)',
    },

    /* Documentation Categories */
    categories: {
        marginBottom: 'var(--spacing-3xl)',
    },

    categoriesGrid: {
        display: 'grid',
        gridTemplateColumns: {
            default: 'repeat(auto-fit, minmax(250px, 1fr))',
            '@media (max-width: 768px)': '1fr',
        },
        gap: {
            default: 'var(--spacing-lg)',
            '@media (max-width: 768px)': 'var(--spacing-md)',
        },
        marginTop: 'var(--spacing-xl)',
    },

    fadeInUp: {
        animationName: fadeInUp,
        animationDuration: 'var(--transition-normal)',
        animationTimingFunction: 'ease-out',
    },

    stagger1: { animationDelay: '0.1s' },
    stagger2: { animationDelay: '0.2s' },
    stagger3: { animationDelay: '0.3s' },
    stagger4: { animationDelay: '0.4s' },
});